import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import { useLocation } from '../../contexts/LocationContext'

// Chart container component
ChartContainer.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

function ChartContainer({ title, description, children, height = 'h-96' }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        )}
      </div>
      <div className={height}>{children}</div>
    </div>
  )
}

// Total Enrollment Trends Chart
function PrimarySchoolEnrollmentTrends({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Aggregate data by year and gender
    const aggregatedData = {}

    data.forEach(record => {
      const year = record.year
      const gender = record.gender
      const total = parseFloat(record.total) || 0

      if (!aggregatedData[year]) {
        aggregatedData[year] = { Boys: 0, Girls: 0 }
      }

      aggregatedData[year][gender] = (aggregatedData[year][gender] || 0) + total
    })

    // Convert to array format for ECharts
    const years = Object.keys(aggregatedData).sort()
    const boysData = years.map(year => aggregatedData[year].Boys || 0)
    const girlsData = years.map(year => aggregatedData[year].Girls || 0)
    const totalData = years.map(
      year =>
        (aggregatedData[year].Boys || 0) + (aggregatedData[year].Girls || 0)
    )

    return {
      years,
      series: [
        {
          name: 'Boys',
          data: boysData,
          type: 'line',
          smooth: true,
          itemStyle: { color: '#3b82f6' },
          lineStyle: { width: 3 },
        },
        {
          name: 'Girls',
          data: girlsData,
          type: 'line',
          smooth: true,
          itemStyle: { color: '#ef4444' },
          lineStyle: { width: 3 },
        },
        {
          name: 'Total',
          data: totalData,
          type: 'line',
          smooth: true,
          itemStyle: { color: '#10b981' },
          lineStyle: { width: 3, type: 'dashed' },
        },
      ],
    }
  }, [data])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      formatter: function (params) {
        let content = `<strong>${params[0].axisValue}</strong><br/>`
        params.forEach(param => {
          content += `${param.seriesName}: ${param.value.toLocaleString()}<br/>`
        })
        return content
      },
    },
    legend: {
      bottom: 10,
      itemGap: 20,
    },
    grid: {
      left: 60,
      right: 30,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      data: chartData?.years || [],
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#6b7280' },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: {
        color: '#6b7280',
        formatter: '{value}',
      },
      splitLine: {
        show: false,
      },
    },
    series: chartData?.series || [],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No enrollment data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// Gender Parity Evolution Chart
function PrimarySchoolGenderParity({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Aggregate data by year
    const aggregatedData = {}

    data.forEach(record => {
      const year = record.year
      const gender = record.gender
      const total = parseFloat(record.total) || 0

      if (!aggregatedData[year]) {
        aggregatedData[year] = { Boys: 0, Girls: 0 }
      }

      aggregatedData[year][gender] = (aggregatedData[year][gender] || 0) + total
    })

    // Calculate parity ratios
    const years = Object.keys(aggregatedData).sort()
    const boysData = years.map(year => aggregatedData[year].Boys || 0)
    const girlsData = years.map(year => aggregatedData[year].Girls || 0)
    const parityRatios = years.map(year => {
      const boys = aggregatedData[year].Boys || 0
      const girls = aggregatedData[year].Girls || 0
      return boys > 0 ? (girls / boys) * 100 : 0
    })

    return {
      years,
      boysData,
      girlsData,
      parityRatios,
    }
  }, [data])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
    },
    legend: {
      bottom: 10,
      itemGap: 20,
    },
    grid: {
      left: 60,
      right: 60,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      data: chartData?.years || [],
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#6b7280' },
      splitLine: {
        show: false,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Enrollment',
        position: 'left',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}',
        },
        splitLine: {
          show: false,
        },
      },
      {
        type: 'value',
        name: 'Parity Ratio (%)',
        position: 'right',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}%',
        },
        splitLine: {
          show: false,
        },
      },
    ],
    series: [
      {
        name: 'Boys',
        type: 'bar',
        yAxisIndex: 0,
        data: chartData?.boysData || [],
        itemStyle: { color: '#3b82f6' },
      },
      {
        name: 'Girls',
        type: 'bar',
        yAxisIndex: 0,
        data: chartData?.girlsData || [],
        itemStyle: { color: '#ef4444' },
      },
      {
        name: 'Parity Ratio',
        type: 'line',
        yAxisIndex: 1,
        data: chartData?.parityRatios || [],
        itemStyle: { color: '#10b981' },
        lineStyle: { width: 3 },
        markLine: {
          silent: true,
          data: [
            {
              yAxis: 100,
              lineStyle: { color: '#f59e0b', type: 'dashed', width: 2 },
              label: { formatter: 'Parity (100%)', position: 'end' },
            },
          ],
        },
      },
    ],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No parity data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// School Performance Trajectory Chart (Small Multiples)
function SchoolPerformanceTrajectory({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Group data by school and calculate enrollment trends
    const schoolData = {}

    data.forEach(record => {
      const school = record.school_name
      const year = record.year
      const total = parseFloat(record.total) || 0

      if (!schoolData[school]) {
        schoolData[school] = {}
      }
      if (!schoolData[school][year]) {
        schoolData[school][year] = 0
      }
      schoolData[school][year] += total
    })

    // Convert to array format and calculate growth rates
    const schoolTrends = Object.entries(schoolData).map(
      ([school, yearData]) => {
        const years = Object.keys(yearData).sort()
        const enrollments = years.map(year => yearData[year])

        // Calculate growth rate (last year vs first year)
        const firstYear = enrollments[0] || 0
        const lastYear = enrollments[enrollments.length - 1] || 0
        const growthRate =
          firstYear > 0 ? ((lastYear - firstYear) / firstYear) * 100 : 0

        return {
          school,
          years,
          enrollments,
          growthRate,
          totalStudents: lastYear,
        }
      }
    )

    // Sort by growth rate (descending)
    schoolTrends.sort((a, b) => b.growthRate - a.growthRate)

    // Take top 12 schools for display
    return schoolTrends.slice(0, 12)
  }, [data])

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No school performance data available</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {chartData.map(school => {
        const option = {
          title: {
            show: false,
            text:
              school.school.length > 25
                ? school.school.substring(0, 25) + '...'
                : school.school,
            textStyle: {
              fontSize: 10,
              fontWeight: 'bold',
              fontFamily:
                'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
            },
            top: 5,
          },
          grid: {
            left: 30,
            right: 10,
            top: 6,
            bottom: 40,
            containLabel: false,
          },
          xAxis: {
            type: 'category',
            data: school.years,
            axisLabel: {
              fontSize: 8,
              fontFamily:
                'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
            },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: {
              show: false,
            },
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              fontSize: 8,
              formatter: '{value},',
              fontFamily:
                'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
            },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: {
              show: false,
            },
          },
          series: [
            {
              data: school.enrollments,
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 3,
              itemStyle: {
                color: school.growthRate > 0 ? '#10b981' : '#ef4444',
              },
              lineStyle: { width: 2 },
            },
          ],
          tooltip: {
            trigger: 'axis',
            formatter: function (params) {
              const point = params[0]
              return `${school.school}<br/>${point.name}: ${point.value} students<br/>Growth: ${school.growthRate.toFixed(1)}%`
            },
          },
        }

        return (
          <div
            key={school.school}
            className="border rounded-lg p-3"
            style={{ height: '160px' }}
          >
            <div className="mb-2">
              <h4
                className="text-sm font-medium text-gray-900 truncate"
                title={school.school}
              >
                {school.school}
              </h4>
            </div>
            <ReactECharts
              option={option}
              style={{ height: '100%', width: '100%' }}
              theme="ug-dashboard"
            />
          </div>
        )
      })}
    </div>
  )
}

// Grade-Level Progression Analysis Chart (P1-P7)
function GradeLevelProgressionAnalysis({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Aggregate data by year and grade level
    const gradeData = {}

    data.forEach(record => {
      const year = record.year
      if (!gradeData[year]) {
        gradeData[year] = { p1: 0, p2: 0, p3: 0, p4: 0, p5: 0, p6: 0, p7: 0 }
      }

      // Sum up enrollment for each grade level
      ;['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7'].forEach(grade => {
        const value = parseFloat(record[grade]) || 0
        gradeData[year][grade] += value
      })
    })

    const years = Object.keys(gradeData).sort()
    const grades = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7']
    const gradeLabels = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7']

    const series = grades.map((grade, index) => ({
      name: gradeLabels[index],
      type: 'line',
      stack: 'Total',
      smooth: true,
      symbol: 'circle',
      areaStyle: {},
      emphasis: { focus: 'series' },
      data: years.map(year => gradeData[year][grade]),
      itemStyle: {
        color: `hsl(${index * 50}, 70%, 50%)`,
      },
    }))

    return { years, series }
  }, [data])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: { backgroundColor: '#6a7985' },
      },
      formatter: function (params) {
        let content = `<strong>${params[0].axisValue}</strong><br/>`
        let total = 0
        params.forEach(param => {
          content += `${param.seriesName}: ${param.value.toLocaleString()}<br/>`
          total += param.value
        })
        content += `<strong>Total: ${total.toLocaleString()}</strong>`
        return content
      },
    },
    legend: {
      bottom: 10,
      itemGap: 15,
    },
    grid: {
      left: 60,
      right: 30,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: chartData?.years || [],
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#6b7280' },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: {
        color: '#6b7280',
        formatter: '{value}',
      },
      splitLine: {
        show: false,
      },
    },
    series: chartData?.series || [],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No progression data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// Geographic Enrollment Heatmap
function GeographicEnrollmentHeatmap({ data }) {
  const location = useLocation()

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Determine the appropriate location field to use based on current selection
    let locationField = 'subcounty' // default
    let locationLabel = 'Subcounty'

    if (location.showAllDistricts) {
      // If viewing all districts, group by district
      locationField = 'district'
      locationLabel = 'District'
    } else if (location.selectedVillage) {
      // If village selected, can't go deeper - show parish level
      locationField = 'parish'
      locationLabel = 'Parish'
    } else if (location.selectedParish) {
      // If parish selected, show villages within that parish
      locationField = 'village'
      locationLabel = 'Village'
    } else if (location.selectedSubcounty) {
      // If subcounty selected, show parishes within that subcounty
      locationField = 'parish'
      locationLabel = 'Parish'
    } else if (location.selectedDistrict) {
      // If district selected, show subcounties within that district
      locationField = 'subcounty'
      locationLabel = 'Subcounty'
    }

    // Group by the determined location field and year
    const locationData = {}

    data.forEach(record => {
      const locationName = record[locationField]
      const year = record.year
      const total = parseFloat(record.total) || 0

      if (!locationName) return // Skip records without the location field

      if (!locationData[locationName]) {
        locationData[locationName] = {}
      }
      if (!locationData[locationName][year]) {
        locationData[locationName][year] = 0
      }
      locationData[locationName][year] += total
    })

    // Convert to heatmap format
    const locations = Object.keys(locationData).sort()
    const years = [...new Set(data.map(d => d.year))].sort()

    if (locations.length === 0 || years.length === 0) {
      return null
    }

    const heatmapData = []
    locations.forEach((locationName, locIndex) => {
      years.forEach((year, yearIndex) => {
        const value = locationData[locationName][year] || 0
        heatmapData.push([yearIndex, locIndex, value])
      })
    })

    // Calculate min/max for color scaling
    const values = heatmapData.map(item => item[2])
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)

    return {
      locations,
      years,
      heatmapData,
      minValue,
      maxValue,
      locationLabel,
    }
  }, [
    data,
    location.showAllDistricts,
    location.selectedDistrict,
    location.selectedSubcounty,
    location.selectedParish,
    location.selectedVillage,
  ])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      position: 'top',
      formatter: function (params) {
        const [yearIndex, locIndex, value] = params.data
        const year = chartData.years[yearIndex]
        const location = chartData.locations[locIndex]
        return `${location}<br/>${year}: ${value.toLocaleString()} students`
      },
    },
    grid: {
      left: Math.max(120, chartData?.locationLabel === 'Village' ? 150 : 120),
      right: 30,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      data: chartData?.years || [],
      splitArea: { show: true },
      axisLabel: { color: '#6b7280' },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'category',
      data: chartData?.locations || [],
      splitArea: { show: true },
      axisLabel: {
        color: '#6b7280',
        fontSize: chartData?.locations?.length > 15 ? 8 : 10,
        formatter: function (value) {
          // Truncate long location names for better display
          return value.length > 20 ? value.substring(0, 17) + '...' : value
        },
      },
      splitLine: {
        show: false,
      },
    },
    visualMap: {
      min: chartData?.minValue || 0,
      max: chartData?.maxValue || 1000,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 10,
      text: ['High', 'Low'],
      inRange: {
        color: ['#f0f9ff', '#0ea5e9', '#0369a1'],
      },
    },
    series: [
      {
        name: 'Enrollment',
        type: 'heatmap',
        data: chartData?.heatmapData || [],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No geographic data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// Geographic Enrollment Heatmap Container with dynamic titles
function GeographicEnrollmentHeatmapContainer({ data }) {
  const location = useLocation()

  const { title, description } = useMemo(() => {
    let locationLevel = 'Subcounties'
    let contextDescription = 'across subcounties'

    if (location.showAllDistricts) {
      locationLevel = 'Districts'
      contextDescription = 'across all districts'
    } else if (location.selectedVillage) {
      locationLevel = 'Parishes'
      contextDescription = 'within parishes'
    } else if (location.selectedParish) {
      locationLevel = 'Villages'
      contextDescription = `within ${location.findLocationById(location.selectedParish)?.name || 'selected parish'} parish`
    } else if (location.selectedSubcounty) {
      locationLevel = 'Parishes'
      contextDescription = `within ${location.findLocationById(location.selectedSubcounty)?.name || 'selected subcounty'} subcounty`
    } else if (location.selectedDistrict) {
      locationLevel = 'Subcounties'
      contextDescription = `within ${location.findLocationById(location.selectedDistrict)?.name || 'selected district'} district`
    }

    return {
      title: `Geographic Enrollment Heatmap (${locationLevel})`,
      description: `Primary school enrollment distribution ${contextDescription} over time (darker = higher enrollment)`,
    }
  }, [
    location,
    location.showAllDistricts,
    location.selectedDistrict,
    location.selectedSubcounty,
    location.selectedParish,
    location.selectedVillage,
    location.findLocationById,
  ])

  return (
    <ChartContainer title={title} description={description}>
      <GeographicEnrollmentHeatmap data={data} />
    </ChartContainer>
  )
}

// Completion Rate Trends (P7 Focus) Chart
function CompletionRateTrends({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Calculate completion rates (P7 enrollment vs P1 enrollment from 6 years prior)
    const yearData = {}

    data.forEach(record => {
      const year = record.year
      const p1 = parseFloat(record.p1) || 0
      const p7 = parseFloat(record.p7) || 0
      const total = parseFloat(record.total) || 0

      if (!yearData[year]) {
        yearData[year] = {
          p1Total: 0,
          p7Total: 0,
          totalEnrollment: 0,
          schools: new Set(),
        }
      }

      yearData[year].p1Total += p1
      yearData[year].p7Total += p7
      yearData[year].totalEnrollment += total
      yearData[year].schools.add(record.school_name)
    })

    const years = Object.keys(yearData).sort()

    // Calculate completion rates and P7 focus metrics
    const completionRates = []
    const p7EnrollmentData = []
    const p7PercentageData = []

    years.forEach(year => {
      const currentYear = yearData[year]
      // Look for P1 enrollment from 6 years ago for completion rate
      const baseYear = (parseInt(year) - 6).toString()
      const baseYearData = yearData[baseYear]

      // Completion rate (P7 now vs P1 from 6 years ago)
      let completionRate = 0
      if (baseYearData && baseYearData.p1Total > 0) {
        completionRate = (currentYear.p7Total / baseYearData.p1Total) * 100
      }

      // P7 as percentage of total enrollment
      const p7Percentage =
        currentYear.totalEnrollment > 0
          ? (currentYear.p7Total / currentYear.totalEnrollment) * 100
          : 0

      completionRates.push(completionRate)
      p7EnrollmentData.push(currentYear.p7Total)
      p7PercentageData.push(p7Percentage)
    })

    return {
      years,
      completionRates,
      p7EnrollmentData,
      p7PercentageData,
    }
  }, [data])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      formatter: function (params) {
        let content = `<strong>${params[0].axisValue}</strong><br/>`
        params.forEach(param => {
          const value =
            param.seriesName.includes('Rate') ||
            param.seriesName.includes('Percentage')
              ? `${param.value.toFixed(1)}%`
              : param.value.toLocaleString()
          content += `${param.seriesName}: ${value}<br/>`
        })
        return content
      },
    },
    legend: {
      bottom: 10,
      itemGap: 20,
    },
    grid: {
      left: 60,
      right: 60,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      data: chartData?.years || [],
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#6b7280' },
      splitLine: {
        show: false,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Students',
        position: 'left',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}',
        },
        splitLine: {
          show: false,
        },
      },
      {
        type: 'value',
        name: 'Percentage (%)',
        position: 'right',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}%',
        },
        splitLine: {
          show: false,
        },
      },
    ],
    series: [
      {
        name: 'P7 Total Enrollment',
        type: 'bar',
        yAxisIndex: 0,
        data: chartData?.p7EnrollmentData || [],
        itemStyle: { color: '#3b82f6' },
      },
      {
        name: 'Completion Rate',
        type: 'line',
        yAxisIndex: 1,
        data: chartData?.completionRates || [],
        itemStyle: { color: '#10b981' },
        lineStyle: { width: 3 },
        symbol: 'circle',
        symbolSize: 6,
      },
      {
        name: 'P7 % of Total',
        type: 'line',
        yAxisIndex: 1,
        data: chartData?.p7PercentageData || [],
        itemStyle: { color: '#f59e0b' },
        lineStyle: { width: 2, type: 'dashed' },
        symbol: 'diamond',
        symbolSize: 5,
      },
    ],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No completion data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// Main Primary School Trends Component
PrimarySchoolTrends.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      school_name: PropTypes.string,
      district: PropTypes.string,
      ownership: PropTypes.string,
      enrollment_male: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      enrollment_female: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      length: PropTypes.number,
      forEach: PropTypes.func,
      map: PropTypes.func,
      reduce: PropTypes.func,
    })
  ),
}

function PrimarySchoolTrends({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">
          No primary school enrollment data available
        </p>
      </div>
    )
  }

  // Calculate summary stats
  const totalRecords = data.length
  const uniqueSchools = new Set(data.map(d => d.school_name)).size
  const uniqueYears = new Set(data.map(d => d.year)).size
  const totalStudents = data.reduce(
    (sum, record) => sum + (parseFloat(record.total) || 0),
    0
  )

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Primary School Enrollment Trends
        </h2>
        <p className="text-gray-600 mb-4">
          Analysis of primary school (P1-P7) enrollment data showing trends over
          time
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {totalRecords.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Records</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {uniqueSchools}
            </div>
            <div className="text-sm text-gray-600">Schools</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {uniqueYears}
            </div>
            <div className="text-sm text-gray-600">Years</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {totalStudents.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Students</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="space-y-6">
        {/* High Priority Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartContainer
            title="Primary School Enrollment Trends Over Time"
            description="Year-over-year primary school enrollment showing boys vs girls patterns across P1-P7 grades"
          >
            <PrimarySchoolEnrollmentTrends data={data} />
          </ChartContainer>

          <ChartContainer
            title="Primary School Gender Parity Evolution"
            description="Boys and girls primary school enrollment with parity ratio tracking (100% = perfect parity)"
          >
            <PrimarySchoolGenderParity data={data} />
          </ChartContainer>
        </div>

        {/* School Performance Trajectory - Full Width */}
        <ChartContainer
          title="Primary School Performance Trajectory"
          description="Individual school enrollment trends showing which primary schools are growing vs declining (top 12 by growth rate)"
          height={'h-[530px]'}
        >
          <SchoolPerformanceTrajectory data={data} />
        </ChartContainer>

        {/* Medium Priority Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartContainer
            title="Grade-Level Progression Analysis (P1-P7)"
            description="Primary school enrollment by grade level showing student progression and potential dropout points"
          >
            <GradeLevelProgressionAnalysis data={data} />
          </ChartContainer>

          <GeographicEnrollmentHeatmapContainer data={data} />
        </div>

        {/* Completion Focus */}
        <ChartContainer
          title="Primary School Completion Rate Trends (P7 Focus)"
          description="P7 completion rates vs P1 enrollment from 6 years prior, showing primary school progression and completion patterns"
        >
          <CompletionRateTrends data={data} />
        </ChartContainer>
      </div>
    </div>
  )
}

export default PrimarySchoolTrends
